<ng-container *transloco="let t">
  @if (trip.isLoading()) {
    <div class="loading-container">
      <div class="spinner"></div>
      <p>{{ t('loading') }}...</p>
    </div>
  }

  @if (!trip.isLoading() && !trip.value()) {
    <div class="not-found-container">
      <div class="not-found-content">
        <h1 class="not-found-title">{{ t('tripNotFound') }}</h1>
        <p class="not-found-description">{{ t('tripNotFoundDescription') }}</p>
        <p class="not-found-subtitle">{{ t('exploreOtherContent') }}</p>
        <a href="https://questatlas.world" target="_blank" rel="noopener noreferrer" class="not-found-button">
          {{ t('goToQuestAtlas') }} →
        </a>
      </div>
    </div>
  }

  @if (trip.value(); as tripData) {
    <!-- Header -->
    <header>
      <q-page-header
        mode="view"
        [buttons]="headerButtons()">
      </q-page-header>
    </header>

    <main class="trip-view-container">
      <!-- Personal Copy Banner -->
      @if (trip.value()?.isCopy) {
        <div class="personal-copy-banner">
          <div class="banner-content">
            <q-svg-wrapper iconPath="project-icons/copy" width="18" height="18" viewBox="0 0 24 24"></q-svg-wrapper>
            <span class="banner-label">{{ t('personalCopy') }}</span>
          </div>
          @if (trip.value()?.originalTrip) {
            <button class="view-original-btn" (click)="viewOriginalTrip()">
              {{ t('viewOriginalTrip') }}
            </button>
          }
        </div>
      }

      <!-- Hero Section -->
      <section class="hero-section"
           [style.background-image]="tripData.tripConfig?.titleImageUrl ? 'url(' + tripData.tripConfig.titleImageUrl + ')' : null"
           [class.clickable-hero]="tripData.tripConfig?.titleImageUrl"
           (click)="tripData.tripConfig?.titleImageUrl && imageUrlOnPreview.set(tripData.tripConfig.titleImageUrl)">
        <div class="hero-overlay">
          <div class="hero-content">
            <h1 class="trip-title">{{ tripData.name }}</h1>
            @if (descriptionMain()) {
              <p class="trip-subtitle">{{ descriptionMain() }}</p>
            }
          </div>
        </div>
      </section>

      @if (descriptionFullParts().length) {
        <section class="common-section regular-16">
          @for (dp of descriptionFullParts(); track $index) {
            <div style="min-height: 24px">{{dp}}</div>
          }
        </section>
      }

      <!-- Stats Section -->
      @if (tripData.tripConfig?.stats) {
        <section class="stats-section" aria-labelledby="stats-title">
          <div class="stats-container">
            <div class="stats-header">
              <h2 id="stats-title" class="section-title">{{ t('tripStats') }}</h2>
              @if (tripData.activityTags && tripData.activityTags.length > 0) {
                <div class="activity-tags-inline">
                  @for (tag of tripData.activityTags; track tag) {
                    <span class="activity-tag">{{ t(tag) }}</span>
                  }
                </div>
              }
            </div>
            <div class="stats-grid">
              @if (formattedDistance()) {
                <div class="stat-card">
                  <div class="stat-icon">
                    <q-svg-wrapper iconPath="project-icons/route" width="28" height="28" viewBox="0 0 24 24"></q-svg-wrapper>
                  </div>
                  <div class="stat-content">
                    <div class="stat-label">{{ t('distance') }}</div>
                    <div class="stat-value">{{ formattedDistance() }} <span class="stat-unit">{{ t('kilometerShort') }}</span></div>
                  </div>
                </div>
              }

              @if (formattedTime()) {
                <div class="stat-card">
                  <div class="stat-icon">
                    <q-svg-wrapper iconPath="project-icons/clock" width="28" height="28" viewBox="0 0 20 20"></q-svg-wrapper>
                  </div>
                  <div class="stat-content">
                    <div class="stat-label">{{ t('duration') }}</div>
                    <div class="stat-value">{{ formattedTime() }}</div>
                  </div>
                </div>
              }

              @if (formattedElevationGain()) {
                <div class="stat-card">
                  <div class="stat-icon">
                    <q-svg-wrapper iconPath="project-icons/arrow" iconId="a" width="28" height="28" viewBox="0 0 24 24" style="transform: rotate(-90deg)"></q-svg-wrapper>
                  </div>
                  <div class="stat-content">
                    <div class="stat-label">{{ t('elevationGain') }}</div>
                    <div class="stat-value">{{ formattedElevationGain() }} <span class="stat-unit">{{ t('meterShort') }}</span></div>
                  </div>
                </div>
              }

              @if (formattedElevationLoss()) {
                <div class="stat-card">
                  <div class="stat-icon descend">
                    <q-svg-wrapper iconPath="project-icons/arrow" iconId="a" width="28" height="28" viewBox="0 0 24 24" style="transform: rotate(-90deg)"></q-svg-wrapper>
                  </div>
                  <div class="stat-content">
                    <div class="stat-label">{{ t('elevationLoss') }}</div>
                    <div class="stat-value">{{ formattedElevationLoss() }} <span class="stat-unit">{{ t('meterShort') }}</span></div>
                  </div>
                </div>
              }

              @if (tripData.tripConfig.stats.pavedRoadsPercent !== undefined) {
                <div class="stat-card">
                  <div class="stat-icon">
                    <q-svg-wrapper iconPath="project-icons/pie-chart" id="c" width="28" height="28" viewBox="0 0 24 24"></q-svg-wrapper>
                  </div>
                  <div class="stat-content">
                    <div class="stat-label">{{ t('pavedRoad') }}</div>
                    <div class="stat-value">{{ tripData.tripConfig.stats.pavedRoadsPercent }}<span class="stat-unit">%</span></div>
                  </div>
                </div>
              }

              @if (tripData.tripConfig.stats.unpavedRoadsPercent !== undefined) {
                <div class="stat-card">
                  <div class="stat-icon">
                    <q-svg-wrapper iconPath="project-icons/pie-chart" width="28" height="28" viewBox="0 0 24 24"></q-svg-wrapper>
                  </div>
                  <div class="stat-content">
                    <div class="stat-label">{{ t('unpavedRoad') }}</div>
                    <div class="stat-value">{{ tripData.tripConfig.stats.unpavedRoadsPercent }}<span class="stat-unit">%</span></div>
                  </div>
                </div>
              }
            </div>
          </div>
        </section>
      }

      <!-- App Download Section -->
      <section class="app-download-section" aria-label="Download app">
        <div class="app-download-container">
          <div class="app-download-content">
            <h3 class="app-download-title">{{ t('followThisTripInOurApp') }}</h3>
            <p class="app-download-subtitle">{{ t('travelWithUs') }}</p>
          </div>
          <nav class="app-download-buttons" aria-label="App store links">
            <a href="https://apps.apple.com/ua/app/questatlas/id6461825089" target="_blank" rel="noopener noreferrer" class="app-store-link" aria-label="Download on the App Store">
              <img style="width: 150px; height: 50px" src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" alt="Download on the App Store"/>
            </a>
            <a href="https://play.google.com/store/apps/details?id=world.app.questatlas" target="_blank" rel="noopener noreferrer" class="app-store-link" aria-label="Get it on Google Play">
              <img style="width: 170px; height: 50px" src="https://upload.wikimedia.org/wikipedia/commons/thumb/7/78/Google_Play_Store_badge_EN.svg/2560px-Google_Play_Store_badge_EN.svg.png" alt="Get it on Google Play"/>
            </a>
          </nav>
        </div>
      </section>

      <!-- Events Section -->
      @if (events.value() && events.value().length > 0) {
        <section class="events-section" aria-labelledby="events-title">
          <div class="content-container">
            <h2 id="events-title" class="section-title">{{ t('events') }}</h2>
            <p class="section-subtitle">{{ t('eventsIncludingThisTrip') }}</p>

            <div class="events-grid">
              @for (event of events.value(); track event.id) {
                <article class="event-card" (click)="openEventInNewTab(event.id)">
                  <div class="event-header">
                    <h3 class="event-name">{{ event.name }}</h3>
                    <div class="event-status"
                         [class.completed]="isEventCompleted(event)"
                         [class.active]="isEventActive(event)"
                         [class.upcoming]="!isEventStarted(event)">
                      @if (isEventCompleted(event)) {
                        {{ t('eventCompleted') }}
                      } @else if (isEventActive(event)) {
                        {{ t('eventActive') }}
                      } @else {
                        {{ t('eventUpcoming') }}
                      }
                    </div>
                  </div>

                  @if (event.description) {
                    <p class="event-description">{{ event.description }}</p>
                  }

                  <footer class="event-meta">
                    <div class="event-meta-item">
                      <span class="meta-label">{{ t('participants') }}:</span>
                      <span class="meta-value">{{ event.slots }}</span>
                    </div>
                    @if (event.endDate) {
                      <div class="event-meta-item">
                        <span class="meta-label">{{ t('endDate') }}:</span>
                        <time class="meta-value" [attr.datetime]="event.endDate.toISOString()">{{ formatEventDate(event.endDate) }}</time>
                      </div>
                    }
                  </footer>
                </article>
              }
            </div>
          </div>
        </section>
      }

      <!-- Map Section -->
      @if (tripData.tripConfig && (tripData.quests?.length > 0 || tripData.obelisks?.length > 0 || tripData.tripConfig.paths?.length > 0 || tripData.tripConfig.poi?.length > 0 || tripData.tripConfig.locations?.length > 0)) {
        <section class="map-section" #mapSection aria-labelledby="map-title">
          <div class="content-container">
            <h2 id="map-title" class="section-title">{{ t('map') }}</h2>
            <div class="map-wrapper">
              <app-display-map
                [options]="mapOptions()"
                [hideGeocoder]="true"
                [centerOnUserPosition]="false"
                (mapInit)="onMapInit($any($event))"
                (clickOnQuest)="onQuestClick($any($event))"
                (clickOnObelisk)="onObeliskClick($any($event))">
              </app-display-map>
            </div>
          </div>
        </section>
      }

      <!-- Locations Section -->
      @if (tripData.tripConfig?.locations && tripData.tripConfig.locations.length > 0) {
        <section class="locations-section" aria-labelledby="locations-title">
          <div class="content-container">
            <h2 id="locations-title" class="section-title">{{ t('locations') }}</h2>

            @for (location of tripData.tripConfig.locations; track location.name; let i = $index) {
              <article class="location-card">
                <div class="location-header">
                  <div class="location-number">{{ i + 1 }}</div>
                  <h3 class="location-name">{{ location.name }}</h3>
                </div>

                <!-- Location Sections -->
                @if (location.sections && location.sections.length > 0) {
                  <div class="location-sections">
                    @for (section of location.sections; track section) {
                      <div class="section-content">
                        @if (section.title) {
                          <h4 class="section-subtitle">{{ section.title }}</h4>
                        }
                        @if (section.subtitle) {
                          <p class="section-text-subtitle">{{ section.subtitle }}</p>
                        }
                        @if (section.description) {
                          <p class="section-description">{{ section.description }}</p>
                        }
                        @if (getSectionImages(section).length > 0) {
                          <figure class="section-images">
                            @for (img of getSectionImages(section); track img) {
                              <figure class="image-wrapper">
                                <img [src]="img.url" [alt]="img.caption || ''" (click)="imageUrlOnPreview.set(img.url)" />
                                @if (img.caption) {
                                  <figcaption class="image-caption">{{ img.caption }}</figcaption>
                                }
                              </figure>
                            }
                          </figure>
                        }
                      </div>
                    }
                  </div>
                }

                <!-- Quests/Obelisks in this location -->
                @if (location.clusters && location.clusters.length > 0) {
                  <div class="clusters-section">
                    <div class="clusters-grid">
                      @for (cluster of location.clusters; track cluster) {
                        <div class="cluster-card" (click)="onClusterClick(cluster)">
                          @if (hasClusterImage(cluster)) {
                            <div class="cluster-image-container">
                              <img [src]="getClusterImage(cluster)" [alt]="cluster.name" />
                            </div>
                          } @else {
                            <div class="cluster-icon">
                              @if (isQuest(cluster)) {
                                <q-svg-wrapper iconPath="project-icons/map" width="20" height="20" viewBox="0 0 20 20"></q-svg-wrapper>
                              } @else {
                                <q-svg-wrapper iconPath="project-icons/place" width="20" height="20" viewBox="0 0 24 24"></q-svg-wrapper>
                              }
                            </div>
                          }
                          <div class="cluster-name">{{ cluster.name }}</div>
                        </div>
                      }
                    </div>
                  </div>
                }

                <!-- Points of Interest -->
                @if (location.poi && location.poi.length > 0) {
                  <div class="poi-section">
                    <h4 class="poi-title">{{ t('pointsOfInterest') }}</h4>
                    <div class="poi-grid">
                      @for (poi of location.poi; track poi.uuid || poi.name) {
                        <div class="poi-card">
                          <div class="poi-icon-image">
                            <img [src]="'/assets/maki/' + poi.icon + '.svg'" [alt]="poi.name" />
                          </div>
                          <div class="poi-content">
                            <div class="poi-name">{{ poi.name }}</div>
                            @if (poi.description) {
                              <p class="poi-description">{{ poi.description }}</p>
                            }
                            @if (poi.website) {
                              <a [href]="poi.website" target="_blank" rel="noopener noreferrer" class="poi-website">
                                {{ t('visitWebsite') }}
                                <svg viewBox="0 0 24 24">
                                  <use [attr.xlink:href]="'assets/project-icons/link.svg#c'"></use>
                                </svg>
                              </a>
                            }
                          </div>
                        </div>
                      }
                    </div>
                  </div>
                }
              </article>
            }
          </div>
        </section>
      }

      <!-- Quests & Obelisks Section (if not in locations) -->
      @if (loadedQuests().length > 0 || loadedObelisks().length > 0) {
        <section class="activities-section" aria-labelledby="activities-title">
          <div class="content-container">
            <h2 id="activities-title" class="section-title">{{ allActivitiesSectionTitle() }}</h2>

            @if (loadedQuests().length > 0) {
              <div class="quests-grid">
                @for (quest of loadedQuests(); track quest.id) {
                  <article class="activity-card" (click)="onQuestCardClick(quest)">
                    <div class="activity-content">
                      <div class="activity-name-container">
                        <div class="cluster-image-container">
                          @if (quest?.info?.images.length) {
                            <img [src]="quest.info?.images[0]?.url" alt="" />
                          }
                        </div>
                        <h4 class="activity-name">{{ quest.name }}</h4>
                      </div>
                      @if (quest.info?.address) {
                        <address class="activity-address">
                          <svg viewBox="0 0 24 24">
                            <use [attr.xlink:href]="'assets/project-icons/location.svg#c'"></use>
                          </svg>
                          {{ quest.info.address }}
                        </address>
                      }
                    </div>
                  </article>
                }
              </div>
            }

            @if (loadedObelisks().length > 0) {
              <div class="obelisks-grid">
                @for (obelisk of loadedObelisks(); track obelisk.uuid) {
                  <article class="activity-card" (click)="onObeliskCardClick(obelisk)">
                    <div class="activity-content">
                      <div class="activity-name-container">
                        <div class="cluster-image-container">
                          @if (obelisk?.images.length) {
                            <img [src]="obeliskImageUri(obelisk)" alt="" />
                          }
                        </div>
                        <h4 class="activity-name">{{ obelisk.name }}</h4>
                      </div>
                    </div>
                  </article>
                }
              </div>
            }
          </div>
        </section>
      }
    </main>

    <!-- Footer -->
    <app-page-footer [showAppDownload]="true"></app-page-footer>

    <!-- Floating Buttons for Small Screens -->
    @if ((selectedQuest() || selectedObelisk()) && smallWidth()) {
      <button class="floating-secondary-button back-btn"
              (click)="closeSidepanel()">
        <svg viewBox="0 0 24 24">
          <use [attr.xlink:href]="'assets/project-icons/chevron-left.svg#c'"></use>
        </svg>
        {{ t('back') }}
      </button>

      <button class="floating-secondary-button desc-btn"
              (click)="sidepanelOpenOnSmall.set(true)">{{ t('description') }}</button>
    }
  }

  <!-- Sidepanel -->
  @if (!smallWidth() || sidepanelOpenOnSmall()) {
    @if (selectedQuest()) {
      <aside class="side-panel" role="complementary" aria-label="Quest details">
        <app-quest-view
          [quest]="selectedQuest()!"
          (previewImage)="imageUrlOnPreview.set($any($event))"
          (closeQuest)="closeQuestSidepanel()"
          (hideEvent)="sidepanelOpenOnSmall.set(false)">
        </app-quest-view>
      </aside>
    } @else if (selectedObelisk()) {
      <aside class="side-panel" role="complementary" aria-label="Place details">
        <app-obelisk-view
          [obelisk]="selectedObelisk()!"
          (previewImage)="imageUrlOnPreview.set($any($event))"
          (closeObelisk)="closeObeliskSidepanel()"
          (hideEvent)="sidepanelOpenOnSmall.set(false)">
        </app-obelisk-view>
      </aside>
    }
  }

  <!-- Image Preview -->
  @if (imageUrlOnPreview()) {
    <dialog class="image-view-container" open (click)="imageUrlOnPreview.set(null); $event.stopPropagation()" aria-label="Image preview">
      <svg class="close-icon" viewBox="0 0 24 24" (click)="imageUrlOnPreview.set(null); $event.stopPropagation()" aria-label="Close preview">
        <use [attr.xlink:href]="'assets/project-icons/close.svg#c'"></use>
      </svg>
      <img [src]="imageUrlOnPreview()!" alt="Preview" (click)="$event.stopPropagation()">
    </dialog>
  }

  <!-- Start for Myself Confirmation Modal -->
  @if (showCopyModal()) {
    <app-modal
      [title]="t('startForMyselfModalTitle')"
      [size]="'medium'"
      [footerButtons]="copyModalButtons"
      (closeEvent)="showCopyModal.set(false)">
      <p class="copy-modal-description">{{ t('startForMyselfModalDescription') }}</p>
    </app-modal>
  }
</ng-container>


